import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IsMobile } from '../../../../../../utility/BrowserCheck';
import useUser from '../../../helpers/useUser';
import useInitialiseEmbed from '../helpers/useInitialiseEmbed';
import { StyledMessage } from '../Unity.styled';
import UnityWebPlayer from './UnityWebPlayer';
import { withFeature } from '../../LaunchDarkly';
import useUnityContext from '../helpers/useUnityContext';
import { useDetectVisibility } from '@core/components/Unity/helpers/useDetectVisibility';
import { useEmbedMessages } from '@core/components/Unity/helpers/useEmbedMessages';

export const SimulationPlayerEmbed = () => {
  const { search } = useLocation();
  const { ID } = useUser();

  const [visible] = useDetectVisibility();
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const context = useUnityContext();

  const [unitID, Hash, debug] = useMemo(() => {
    const params = new URLSearchParams(search);

    return [
      params.get('unitID'),
      params.get('Hash'),
      Boolean(params.get('debug')) || false,
    ];
  }, [search]);

  const { simulationData } = useInitialiseEmbed(Hash);

  const [send, postEvent] = useEmbedMessages({
    context,
    Hash,
    visible,
    debug,
  });

  useEffect(() => {
    send('PlayerVisible', visible);

    postEvent('onPlayerVisible')(visible);
  }, [postEvent, send, visible]);

  useEffect(() => {
    /**
     * @param {MessageEvent} event
     */
    const handleEvents = (event) => {
      const { Hash: EventHash, event: eventName, data } = event.data;
      if (EventHash !== Hash) {
        return;
      }
      switch (eventName) {
        // case 'updateSimulation':
        //   return send('UpdateSimData', data);
        case 'setMixpanelId':
          return send('SetMixpanelID', data);

        default:
        // nothing
      }
    };
    window.addEventListener('message', handleEvents);

    return () => {
      window.removeEventListener('message', handleEvents);
    };
  }, [Hash, send]);

  useEffect(() => {
    /** @type {any} */ (document).unityWebAppUnitID = unitID || null;
  }, [unitID]);

  useEffect(() => {
    if (!context) {
      return;
    }

    const handleComplete = postEvent('onComplete');
    context.addEventListener('OnSessionComplete', handleComplete);

    const handleEnd = postEvent('onEnd');
    context.addEventListener('OnEndSim', handleEnd);

    const handleSubmissionId = postEvent('onSubmissionID');
    context.addEventListener('OnSubmissionIDAssigned', handleSubmissionId);

    const handleExitSimData = postEvent('onExitSimData');
    context.addEventListener('OnExitSimData', handleExitSimData);

    const handlePlayerLoaded = postEvent('onPlayerLoaded');
    context.addEventListener('OnPlayerLoaded', handlePlayerLoaded);

    const handleScoreAvailable = postEvent('onScoreAvailable');
    context.addEventListener('OnScoreAvailable', handleScoreAvailable);

    return () => {
      context.removeEventListener('OnSessionComplete', handleComplete);
      context.removeEventListener('OnEndSim', handleEnd);
      context.removeEventListener('OnSubmissionIDAssigned', handleSubmissionId);
      context.removeEventListener('OnExitSimData', handleExitSimData);
      context.removeEventListener('OnPlayerLoaded', handlePlayerLoaded);
      context.removeEventListener('OnScoreAvailable', handleScoreAvailable);
    };
  }, [postEvent, context]);

  if (!simulationData) {
    return null;
  }

  if (IsMobile()) {
    return (
      <StyledMessage>
        Unfortunately, web preview is currently only available in latest version
        of Google Chrome, Mozilla Firefox, and Microsoft Edge on a computer.
      </StyledMessage>
    );
  }

  return (
    <UnityWebPlayer
      simulationData={simulationData}
      context={context}
      publicFlag={!ID || ID === '0'}
    />
  );
};

// needs feature HOC to not load this component until features are available
export default withFeature(['unity-build-path'])(SimulationPlayerEmbed);
